// src/features/procedures/components/steps/AIImageAnalysisStep.jsx
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Box, Typography, CircularProgress } from '@mui/material';
import { Psychology as AIIcon } from '@mui/icons-material';

/**
 * AI Image Analysis Step Component
 * (Uses same upload as FILE_UPLOAD for now)
 * 
 * Options Config:
 * {
 *   "confidence_threshold": 0.8,    // Minimum confidence score to accept
 *   "auto_proceed": true,           // Automatically proceed if confidence is high
 *   "show_raw_output": false        // Show raw AI output to user
 * }
 * 
 * Note: This component currently renders the same as FileUploadStep.
 * The AI analysis would happen on the backend after file upload.
 */
import FileUploadStep from './FileUploadStep';

export default function AIImageAnalysisStep({ step, value, onChange, disabled, preview, procedureName, taskId }) {
  const { t } = useTranslation(['procedures']);
  const options = step.options || {};
  const aiModel = step.ai_model || 'Unknown Model';

  if (preview) {
    return (
      <Box>
        <Alert severity="info" icon={<AIIcon />}>
          <Typography variant="body2" fontWeight="medium">
            {step.label}
          </Typography>
          <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
            {t('procedures:step_options.ai_model_label', { model: aiModel })}
          </Typography>
          {step.description && (
            <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 1, whiteSpace: 'pre-wrap' }}>
              {step.description}
            </Typography>
          )}
        </Alert>
      </Box>
    );
  }

  // Show AI analysis results if available
  if (value?.ai_result) {
    return (
      <Box>
        <Typography variant="body2" gutterBottom>
          {step.label}
        </Typography>
        
        <Alert severity="success" icon={<AIIcon />} sx={{ mt: 2 }}>
          <Typography variant="body2" fontWeight="medium">
            {t('procedures:step_options.ai_analysis_complete')}
          </Typography>
          {value.ai_result.confidence && (
            <Typography variant="caption" display="block">
              {t('procedures:step_options.confidence_label', { confidence: (value.ai_result.confidence * 100).toFixed(1) })}
            </Typography>
          )}
          {options.show_raw_output && value.ai_result.output && (
            <Box sx={{ mt: 1, p: 1, bgcolor: 'background.default', borderRadius: 1 }}>
              <pre style={{ margin: 0, fontSize: '0.75rem', whiteSpace: 'pre-wrap' }}>
                {JSON.stringify(value.ai_result.output, null, 2)}
              </pre>
            </Box>
          )}
        </Alert>
      </Box>
    );
  }

  // Show processing state
  if (value?.processing) {
    return (
      <Box>
        <Typography variant="body2" gutterBottom>
          {step.label}
        </Typography>
        
        <Alert severity="info" icon={<CircularProgress size={20} />} sx={{ mt: 2 }}>
          <Typography variant="body2">
            AI is analyzing the image...
          </Typography>
          <Typography variant="caption" display="block" color="text.secondary">
            {t('procedures:step_options.ai_model_label', { model: aiModel })}
          </Typography>
        </Alert>
      </Box>
    );
  }

  // Default to file upload component for initial upload
  return (
    <Box>
      <Typography variant="caption" color="secondary" display="flex" alignItems="center" gap={0.5} sx={{ mb: 1 }}>
        <AIIcon fontSize="small" />
        {t('procedures:step_options.ai_model_label', { model: aiModel })}
      </Typography>
      <FileUploadStep
        step={step}
        value={value}
        onChange={onChange}
        disabled={disabled}
        preview={preview}
        procedureName={procedureName}
        taskId={taskId}
      />
    </Box>
  );
}